import type { CurrentUser } from "@/lib/auth";
import {
  getUserLocalLogin,
  normalizeEmployeeLogin,
  userHasGroup,
  userIsAbrechnung,
  userIsBuero,
  userIsManagement,
} from "@/lib/auth";

/**
 * Sonderregelung: privates Überstunden-/Stundenkonto nur für diesen Login sichtbar.
 * (flb@ernsthaus.de → kanonisch „flb“)
 */
export const PRIVATE_TIME_ACCOUNT_LOGIN = "flb";

export function isPrivateTimeAccountLogin(login: string | null | undefined): boolean {
  return normalizeEmployeeLogin(login || "") === PRIVATE_TIME_ACCOUNT_LOGIN;
}

/** Fremde Nutzer dürfen Stunden/Überstunden des privaten Kontos nicht sehen. */
export function canViewEmployeeTimeAccount(
  viewer: CurrentUser | null | undefined,
  targetLogin: string | null | undefined,
): boolean {
  if (!isPrivateTimeAccountLogin(targetLogin)) return true;
  return getUserLocalLogin(viewer ?? null) === PRIVATE_TIME_ACCOUNT_LOGIN;
}

/** Arbeitszeitmodelle / Zuweisungen – Büro, Admins und Tester. */
export function userCanManageZeiterfassungSettings(user: CurrentUser | null): boolean {
  if (!user) return false;
  return (
    userHasGroup(user, "App-Admins") ||
    userHasGroup(user, "App-Tester") ||
    userIsBuero(user)
  );
}

/** Zeiten aller Mitarbeiter erfassen und ändern – Büro und App-Admins. */
export function userCanManageZeiterfassungEntries(user: CurrentUser | null): boolean {
  if (!user) return false;
  return userHasGroup(user, "App-Admins") || userIsBuero(user);
}

/** Geschäftsführung und App-Admins: Abwesenheitsfreigaben + Einsicht Arbeitszeitverstöße. */
export function userCanApproveZeiterfassung(user: CurrentUser | null): boolean {
  if (!user) return false;
  return userIsManagement(user) || userHasGroup(user, "App-Admins");
}

/**
 * Büro-Bereich „Alle Abwesenheitsanträge“ inkl. endgültigem Löschen –
 * Büro, Geschäftsführung und App-Admins.
 */
export function userCanManageAllAbsences(user: CurrentUser | null): boolean {
  if (!user) return false;
  return userHasGroup(user, "App-Admins") || userIsBuero(user) || userIsManagement(user);
}

/** Abwesenheits-Kontingente (Urlaubstage etc.) – Abrechnung und Geschäftsführung. */
export function userCanManageAbsenceBudgets(user: CurrentUser | null): boolean {
  if (!user) return false;
  return userHasGroup(user, "App-Admins") || userIsAbrechnung(user) || userIsManagement(user);
}

/** Überstunden-Startstände und Auszahlungen – Büro, Abrechnung, GF, Admins. */
export function userCanManageOvertimeOpeningBalances(user: CurrentUser | null): boolean {
  if (!user) return false;
  return (
    userHasGroup(user, "App-Admins") ||
    userIsBuero(user) ||
    userIsAbrechnung(user) ||
    userIsManagement(user)
  );
}

/** Mitarbeiterverwaltung (Kontingente, Startstände, Stammdaten). */
export function userCanManageMitarbeiter(user: CurrentUser | null): boolean {
  if (!user) return false;
  return (
    userCanManageZeiterfassungEntries(user) ||
    userCanManageAbsenceBudgets(user) ||
    userCanManageOvertimeOpeningBalances(user) ||
    userCanManageZeiterfassungSettings(user)
  );
}
